
import React from 'react'
import GSSVG from 'smartstream-widgets/controls/GSSVG'
import MarketSelection from 'smartstream-widgets/controls/MarketSelection/MarketSelection'
import { Button, Grid, Typography } from '@mui/material'

import { PropTranslations } from 'smartstream-widgets/types/widget'
import { AffiliateLabel, BookmakerFormatted, OddsFormat, SelectionsFormatted } from './types'

export function renderAffiliate(
  affiliateLabel: AffiliateLabel,
  classes: {
    [K in string]: string
  },
  message: string | null,
  offerText: string | null,
  showOfferTextLast: boolean,
  translations: PropTranslations,
  bookmakerLink: string | null,
  isBookmakerLinkEnabled: boolean
) {
  const labels = {
    custom: message,
    featured: translations.featured,
    oddsby: translations.affiliateCaption,
    offertext: offerText,
    bookmakerLink: bookmakerLink,
    isBookmakerLinkEnabled: isBookmakerLinkEnabled
  }

  return (
    <>
      {
        affiliateLabel &&
        <Typography
          className={
            affiliateLabel === 'featured'
              ? classes.featuredLabel
              : classes.affiliateText
          }
          style={{
            order: affiliateLabel === 'offertext' && showOfferTextLast ? 1 : 0
          }}
        >
          {labels.bookmakerLink && labels.isBookmakerLinkEnabled ?
            <a href={`${bookmakerLink}`} target="_blank">{labels[affiliateLabel]}</a> :
            labels[affiliateLabel]}
        </Typography>
      }
    </>
  )
}

export function MarketLineDrawLayout(
  animationKey: string,
  BASE_ASSET_PATH: string,
  bookmaker: BookmakerFormatted,
  isBookmakerLinkEnabled: boolean,
  classes: {
    [K in string]: string
  },
  labelSet: string,
  oddsFormat: OddsFormat,
  selections: SelectionsFormatted,
  showIconOfferText: boolean,
  translations: PropTranslations
) {

  let selectionLabels = {
    leftLabel: '',
    drawLabel: '',
    rightLabel: ''
  }

  if (labelSet === 'american') {
    selectionLabels = {
      leftLabel: translations.away,
      drawLabel: translations.tie,
      rightLabel: translations.home
    }
  } else if (labelSet === 'european') {
    selectionLabels = {
      leftLabel: '1',
      drawLabel: 'X',
      rightLabel: '2'
    }
  }

  const { leftLabel, rightLabel, drawLabel } = selectionLabels
  const { leftSelection, rightSelection, drawSelection } = selections
  const {
    showAffiliateLogo,
    bookmakerLogoUrl,
    bookmakerLink,
    iconUrl,
    affiliateLabel,
    message,
    name,
    sourceId,
    offerText
  } = bookmaker

  return (
    <>
      {
        showAffiliateLogo && bookmakerLogoUrl && !iconUrl &&
        <Grid alignItems='center' justifyContent='center' container className={`${classes.topAffiliateContainer}`} data-testid="affiliate-logo">
          {renderAffiliate(affiliateLabel, classes, message, offerText, false, translations, bookmakerLink, isBookmakerLinkEnabled)}
          <img
            src={bookmakerLogoUrl}
            alt={name}
            className={classes.topAffiliateLogo}
          />
        </Grid>
      }
      <div className={`${bookmakerLink ? classes.rootDrawLink : classes.rootDraw} ${labelSet ? classes.drawWithLabel : ''}`}>
        {
          iconUrl &&
          <Grid container alignItems={'center'} className={classes.iconContainer}>
            {
              bookmakerLink ?
                <Button
                  className={`${classes.iconLink} ${classes.iconButton}`}
                  variant='text'
                  color='primary'
                  href={bookmakerLink}
                  target={'_blank'}
                >
                  <span className={classes.iconLabel}>
                    <img
                      src={iconUrl}
                      alt={name}
                      className={classes.icon}
                    />
                  </span>
                </Button> :
                <img
                  src={iconUrl}
                  alt={name}
                  className={classes.icon}
                />
            }
          </Grid>
        }
        <Grid
          container
          alignItems={'center'}
          justifyContent={'space-between'}
          className={classes.oddsContainerDraw}
        >
          {
            leftSelection &&
            <MarketSelection
              animationKey={`${animationKey}${leftSelection.outcome}`}
              classes={{
                oddButton: classes.oddButtonWithDraw,
                captionOdd: classes.captionOdd,
                buttonBase: `${leftSelection.isHighest ? classes.bestSelection : classes.buttonBase}`,
                buttonDisabled: classes.buttonDisabled
              }}
              label={leftLabel}
              oddsFormat={oddsFormat}
              selection={leftSelection}
              sourceId={sourceId}
            />
          }
          {
            drawSelection &&
            <MarketSelection
              animationKey={`${animationKey}${drawSelection.outcome}`}
              classes={{
                oddButton: classes.oddButtonWithDraw,
                captionOdd: classes.captionOdd,
                buttonBase: `${drawSelection.isHighest ? classes.bestSelection : classes.buttonBase}`,
                buttonDisabled: classes.buttonDisabled
              }}
              label={drawLabel}
              oddsFormat={oddsFormat}
              selection={drawSelection}
              sourceId={sourceId}
            />
          }
          {
            rightSelection &&
            <MarketSelection
              animationKey={`${animationKey}${rightSelection.outcome}`}
              classes={{
                oddButton: classes.oddButtonWithDraw,
                captionOdd: classes.captionOdd,
                buttonBase: `${rightSelection.isHighest ? classes.bestSelection : classes.buttonBase}`,
                buttonDisabled: classes.buttonDisabled
              }}
              label={rightLabel}
              oddsFormat={oddsFormat}
              selection={rightSelection}
              sourceId={sourceId}
            />
          }
        </Grid>
        {iconUrl && showIconOfferText &&
          <Typography className={classes.iconOfferText}>
            {affiliateLabel === 'featured' &&
              <GSSVG
                className={classes.featuredStar}
                xlinkHref={`${BASE_ASSET_PATH}/img/icons.svg#featured-star`}
              />
            }
            {bookmakerLink ?
              <a href={`${bookmakerLink}`}>{offerText}</a> :
              offerText}
          </Typography>
        }
      </div>
    </>
  )
}

export function MarketLineLayout(
  animationKey: string,
  bookmaker: BookmakerFormatted,
  isBookmakerLinkEnabled: boolean,
  classes: {
    [K in string]: string
  },
  oddsFormat: OddsFormat,
  selections: SelectionsFormatted,
  translations: PropTranslations
) {
  const { leftSelection, rightSelection } = selections
  const {
    showAffiliateLogo,
    bookmakerLogoUrl,
    bookmakerLink,
    marketType,
    affiliateLabel,
    message,
    name,
    sourceId,
    offerText,
    iconUrl
  } = bookmaker

  const affiliateLogoUrl = iconUrl || bookmakerLogoUrl
  const affiliateLogoClass = iconUrl ? classes.icon : classes.logo
  const bookmakerLogoNode = (
    <img
      src={affiliateLogoUrl}
      alt={name}
      className={affiliateLogoClass}
    />
  )

  return (
    <Grid
      alignItems='center'
      className={classes.root}
      container
      justifyContent='space-between'
      wrap='nowrap'
      data-testid='market-line-container'
    >
      {
        leftSelection && <MarketSelection
          animationKey={`${animationKey}${leftSelection.outcome}`}
          classes={{
            oddButton: classes.oddButton,
            captionOdd: classes.captionOdd,
            buttonBase: `${leftSelection.isHighest ? classes.bestSelection : classes.buttonBase}`,
            buttonDisabled: classes.buttonDisabled
          }}
          oddsFormat={oddsFormat}
          selection={leftSelection}
          sourceId={sourceId}
        />
      }
      {
        showAffiliateLogo && affiliateLogoUrl ?
          <Grid container direction='column' alignItems='center' wrap='nowrap' className={classes.bookmakerContainer} data-testid='affiliate-logo'>
            {renderAffiliate(affiliateLabel, classes, message, offerText, true, translations, bookmakerLink, isBookmakerLinkEnabled)}
            {bookmakerLink ?
              <Button
                className={classes.iconButton}
                variant='text'
                color='primary'
                href={bookmakerLink}
                target={'_blank'}
              >
                <span className={classes.betLabel}>
                  {bookmakerLogoNode}
                </span>
              </Button> :
              <>
                {bookmakerLogoNode}
              </>
            }
          </Grid> :
          <Typography className={classes.marketLabel}>
            {marketType && translations[marketType]}
          </Typography>
      }
      {
        rightSelection && <MarketSelection
          animationKey={`${animationKey}${rightSelection.outcome}`}
          classes={{
            oddButton: classes.oddButton,
            captionOdd: classes.captionOdd,
            buttonBase: `${rightSelection.isHighest ? classes.bestSelection : classes.buttonBase}`,
            buttonDisabled: classes.buttonDisabled
          }}
          oddsFormat={oddsFormat}
          selection={rightSelection}
          sourceId={sourceId}
        />
      }
    </Grid>
  )
}
